import React from 'react';
import clsx from 'clsx';
import LoadingSpinner from './LoadingSpinner';

type ButtonVariant =
  | 'default'
  | 'destructive'
  | 'outline'
  | 'secondary'
  | 'ghost'
  | 'link'
  | 'primary'
  | 'danger';

type ButtonSize = 'xs' | 'sm' | 'default' | 'lg' | 'icon' | 'md';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'default',
  size = 'default',
  loading = false,
  children,
  className,
  disabled,
  ...props
}) => {
  const normalizedVariant: Exclude<ButtonVariant, 'primary' | 'danger'> =
    variant === 'primary' ? 'default' : variant === 'danger' ? 'destructive' : variant;

  const normalizedSize: Exclude<ButtonSize, 'md'> = size === 'md' ? 'default' : size;

  const baseClasses =
    'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

  const variantClasses = {
    default: 'bg-primary text-primary-foreground hover:opacity-90',
    destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
    outline: 'border border-input bg-background hover:bg-secondary hover:text-secondary-foreground',
    secondary: 'bg-secondary text-secondary-foreground hover:opacity-80',
    ghost: 'hover:bg-secondary hover:text-secondary-foreground',
    link: 'text-primary underline-offset-4 hover:underline',
  };

  const sizeClasses = {
    xs: 'h-7 rounded-md px-2.5 text-xs',
    sm: 'h-9 rounded-md px-3',
    default: 'h-10 px-4 py-2',
    lg: 'h-11 rounded-md px-8 text-base',
    icon: 'h-10 w-10 p-0',
  };

  return (
    <button
      className={clsx(
        baseClasses,
        variantClasses[normalizedVariant],
        sizeClasses[normalizedSize],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <LoadingSpinner 
          size="sm" 
          className="mr-2 text-current" 
        />
      )}
      {children}
    </button>
  );
};

export default Button;
